import axios, { AxiosError, Method } from 'axios';
import Toast from 'components/Shared/Toast';
import ApiError from 'errors/api';
import { apiRequestFormatter } from 'formatters/apiRequest';
import * as rxjs from 'rxjs';
import RxOp from 'rxjs-operators';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  shareReplay,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import { API_ENDPOINT, IS_DEVELOPMENT, REACT_SECRET, REACT_TOKEN } from '../settings';
import authService from './auth';
import tokenService, { ITokens } from './token';
import userService from 'services/userService';

const ORIGIN_BUILD_VERSION = '1104';
const ORIGIN_BUILD_COMMIT = '8559b1c57e';

export class ApiService {
  constructor(private apiEndpoint: string) {}

  private tokenRefreshRequest$: rxjs.Observable<ITokens>;
  private sessionInvalidated = false;

  public get<T = any>(url: string, params?: any): rxjs.Observable<T> {
    return this.request<T>('GET', url, params).pipe(
      map(({ response }) => response),
      filter(response => response !== undefined),
    );
  }

  public post<T = any>(url: string, body: any): rxjs.Observable<T> {
    return this.request<T>('POST', url, body).pipe(
      map(({ response }) => response),
      filter(response => response !== undefined),
    );
  }

  public put<T = any>(url: string, body: any): rxjs.Observable<T> {
    return this.request<T>('PUT', url, body).pipe(
      map(({ response }) => response),
      filter(response => response !== undefined),
    );
  }

  public delete<T = any>(url: string, params?: any): rxjs.Observable<T> {
    return this.request<T>('DELETE', url, params).pipe(
      map(({ response }) => response),
      filter(response => response !== undefined),
    );
  }

  public upload<T = any>(url: string, data: FormData) {
    return this.request<T>('POST', url, data);
  }

  private request<T = any>(
    method: Method,
    url: string,
    data: any = null,
    retry: boolean = true,
  ): rxjs.Observable<{ response: T; progress: number }> {
    if (this.sessionInvalidated) {
      return rxjs.EMPTY;
    }

    const progress$ = new rxjs.BehaviorSubject(0);

    return rxjs.of(true).pipe(
      RxOp.switchMap(() => this.getBearerToken()),
      first(),
      map(token => {
        return {
          token: REACT_TOKEN,
          secret: REACT_SECRET,
          'Origin-Build-Version': ORIGIN_BUILD_VERSION,
          'Origin-Build-Commit': ORIGIN_BUILD_COMMIT,
          Authorization: token,
          'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json',
        };
      }),
      switchMap(headers => {
        return axios.request({
          baseURL: this.apiEndpoint,
          url,
          method,
          headers,
          params: method === 'GET' ? apiRequestFormatter(data) : null,
          data: method === 'POST' || method === 'PUT' ? apiRequestFormatter(data) : null,
          onUploadProgress: (progress: ProgressEvent) => {
            const result = progress.loaded / progress.total;
            progress$.next(result * 100);
          },
        });
      }),
      tap(() => progress$.next(100)),
      map(res => res.data || null),
      startWith(undefined),
      combineLatest(progress$.pipe(distinctUntilChanged()), (response, progress) => ({ response, progress })),
      catchError(err => {
        progress$.complete();
        return this.handleError(err, retry);
      }),
    );
  }

  private getBearerToken() {
    return rxjs.combineLatest([tokenService.getTokens(), userService.getUser()]).pipe(
      first(),
      switchMap(([tokens, user]) => {
        if (!tokens || !user) return rxjs.of(null);

        const now = (Date.now().valueOf() + 500) / 1000;

        if (!user.exp || user.exp > now) {
          return rxjs.of(tokens.jwt_token);
        }

        if (!this.tokenRefreshRequest$) {
          this.tokenRefreshRequest$ = rxjs
            .from(
              this.request<{ data: { jwt_token: string; jwt_refresh_token: string } }>(
                'POST',
                '/auth_v2/refresh',
                { jwt_refresh_token: tokens.jwt_refresh_token },
                true,
              ),
            )
            .pipe(
              map(({ response }) => response),
              filter(response => response !== undefined),
              switchMap(({ data }) =>
                tokenService.setTokens({
                  jwt_token: data.jwt_token,
                  jwt_refresh_token: data.jwt_refresh_token,
                }),
              ),
              tap(() => (this.tokenRefreshRequest$ = null)),
              catchError(() => {
                this.tokenRefreshRequest$ = null;
                this.clearCredentials();
                window.location.reload();
                return rxjs.NEVER;
              }),
              shareReplay(1),
            );
        }

        return this.tokenRefreshRequest$.pipe(map(tokens => tokens.jwt_token));
      }),
      map(accessToken => (accessToken ? `Bearer ${accessToken}` : null)),
    );
  }

  private handleError(err: AxiosError, retry: boolean) {
    if (!err.config || !err.response) return rxjs.throwError(err);

    if (err.response.status === 401) {
      return this.handleSessionInvalidated();
    }

    if (err.response.status === 400 || !retry) {
      return rxjs.throwError(err);
    }

    if (err.response.status === 404 || !retry) return rxjs.throwError(err);
    if (err.response.status === 503 || !retry) return rxjs.throwError(err);

    return userService.getUser().pipe(
      skip(1),
      switchMap(user => {
        if (!user) {
          return rxjs.throwError(new ApiError(err.config, err.response, err));
        }
        return this.request(err.config.method, err.config.url, err.config.data || err.config.params, false);
      }),
    );
  }

  private handleSessionInvalidated(): rxjs.Observable<never> {
    if (this.sessionInvalidated) {
      return rxjs.EMPTY;
    }

    this.sessionInvalidated = true;
    this.clearCredentials();
    authService.logout();

    return rxjs.EMPTY;
  }

  private clearCredentials(): void {
    try {
      tokenService.clearToken();
    } catch {
      // noop
    }
  }

  public handleErrorMessage(err: any) {
    if (IS_DEVELOPMENT) {
      console.log('err object is: ', err);
    }
    if (err?.response?.status === 400 && err?.response?.data?.code === 'ERR_CUSTOM') {
      const errData = err?.response?.data;
      let errMsg =
        errData?.details && Array.isArray(errData.details) && errData.details[0] ? errData.details[0] : errData.details;

      errMsg = errMsg ? errMsg : errData.message ? errData.message : 'Ocorreu um erro. Tente novamente';

      Toast.error(errMsg);
      return;
    }
    if (err.message === 'Request failed with status code 403') {
      Toast.error(
        'Permissão Negada. O administrador desta conta não lhe concedeu privilégio para acessar este recurso',
        6000,
      );
    } else {
      if (err.details !== undefined) {
        const errMsg = Array.isArray(err.details) ? err.details[0] : err.details;
        Toast.error(errMsg);
      } else {
        Toast.error('Ocorreu um erro. Tente novamente');
      }
    }
  }
}

const apiService = new ApiService(API_ENDPOINT);
export default apiService;
